import { useRef, useMemo, useEffect } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

interface ParticleFieldProps {
  count?: number;
  color?: string;
  size?: number;
  spread?: number;
  speed?: number;
  mouseInfluence?: number;
}

export function ParticleField({
  count = 3000,
  color = '#B08080',
  size = 0.015,
  spread = 20,
  speed = 0.3,
  mouseInfluence = 0.5,
}: ParticleFieldProps) {
  const meshRef = useRef<THREE.Points>(null);
  const mouseRef = useRef({ x: 0, y: 0 });
  const scrollRef = useRef({ progress: 0, velocity: 0 });

  // Listen for scroll events to adjust particle behavior
  useEffect(() => {
    const handleScroll = () => {
      // Calculate scroll progress toward demo section (approximate center of page)
      const demoSection = document.getElementById('demo');
      if (demoSection) {
        const rect = demoSection.getBoundingClientRect();
        const viewportCenter = window.innerHeight / 2;
        const distToCenter = Math.abs(rect.top + rect.height / 2 - viewportCenter);
        const maxDist = window.innerHeight;
        // Progress: 1 when demo is centered, 0 when far away
        const progress = Math.max(0, 1 - distToCenter / maxDist);
        const prevProgress = scrollRef.current.progress;
        scrollRef.current.velocity = (progress - prevProgress) * 0.5;
        scrollRef.current.progress = progress;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial call

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Track mouse position
  useMemo(() => {
    const handleMouseMove = (e: MouseEvent) => {
      mouseRef.current.x = (e.clientX / window.innerWidth) * 2 - 1;
      mouseRef.current.y = -(e.clientY / window.innerHeight) * 2 + 1;
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Generate particle positions and velocities
  const { positions, velocities, originalPositions } = useMemo(() => {
    const positions = new Float32Array(count * 3);
    const velocities = new Float32Array(count * 3);
    const originalPositions = new Float32Array(count * 3);

    for (let i = 0; i < count; i++) {
      const i3 = i * 3;
      // Sphere distribution with more density in center
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      const r = Math.pow(Math.random(), 0.5) * spread;

      positions[i3] = r * Math.sin(phi) * Math.cos(theta);
      positions[i3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      positions[i3 + 2] = r * Math.cos(phi);

      originalPositions[i3] = positions[i3];
      originalPositions[i3 + 1] = positions[i3 + 1];
      originalPositions[i3 + 2] = positions[i3 + 2];

      // Random velocities for drift
      velocities[i3] = (Math.random() - 0.5) * 0.02;
      velocities[i3 + 1] = (Math.random() - 0.5) * 0.02;
      velocities[i3 + 2] = (Math.random() - 0.5) * 0.02;
    }

    return { positions, velocities, originalPositions };
  }, [count, spread]);

  // Generate random sizes for variation
  const sizes = useMemo(() => {
    const sizes = new Float32Array(count);
    for (let i = 0; i < count; i++) {
      sizes[i] = Math.random() * size + size * 0.5;
    }
    return sizes;
  }, [count, size]);

  useFrame((state) => {
    if (!meshRef.current) return;

    const time = state.clock.elapsedTime * speed;
    const geometry = meshRef.current.geometry;
    const positionAttr = geometry.attributes.position as THREE.BufferAttribute;
    const posArray = positionAttr.array as Float32Array;

    // Get scroll-based velocity boost (max +15%)
    const scrollProgress = scrollRef.current.progress;
    const scrollBoost = 1 + scrollProgress * 0.15;

    for (let i = 0; i < count; i++) {
      const i3 = i * 3;

      // Base movement - slow drift with scroll-based boost
      const baseVelX = velocities[i3] + Math.sin(time + i * 0.1) * 0.002;
      const baseVelY = velocities[i3 + 1] + Math.cos(time + i * 0.1) * 0.002;
      const baseVelZ = velocities[i3 + 2] + Math.sin(time * 0.5 + i * 0.05) * 0.001;

      posArray[i3] += baseVelX * scrollBoost;
      posArray[i3 + 1] += baseVelY * scrollBoost;
      posArray[i3 + 2] += baseVelZ * scrollBoost;

      // Scroll-based inward drift when approaching demo
      if (scrollProgress > 0.3) {
        const driftStrength = (scrollProgress - 0.3) * 0.003;
        posArray[i3] -= posArray[i3] * driftStrength;
        posArray[i3 + 1] -= (posArray[i3 + 1] + 2) * driftStrength; // Drift downward slightly
      }

      // Mouse influence - particles are attracted/repelled
      const dx = mouseRef.current.x * 10 - posArray[i3];
      const dy = mouseRef.current.y * 10 - posArray[i3 + 1];
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist < 8) {
        const force = (1 - dist / 8) * mouseInfluence * 0.02;
        posArray[i3] += dx * force;
        posArray[i3 + 1] += dy * force;
      }

      // Boundary check - wrap around
      const maxDist = spread * 1.2;
      const currentDist = Math.sqrt(
        posArray[i3] ** 2 + posArray[i3 + 1] ** 2 + posArray[i3 + 2] ** 2
      );
      if (currentDist > maxDist) {
        posArray[i3] = originalPositions[i3];
        posArray[i3 + 1] = originalPositions[i3 + 1];
        posArray[i3 + 2] = originalPositions[i3 + 2];
      }
    }

    positionAttr.needsUpdate = true;

    // Rotate entire system slowly
    meshRef.current.rotation.y += 0.0003;
    meshRef.current.rotation.x = Math.sin(time * 0.2) * 0.1;
  });

  return (
    <points ref={meshRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          args={[positions, 3]}
        />
        <bufferAttribute
          attach="attributes-size"
          args={[sizes, 1]}
        />
      </bufferGeometry>
      <pointsMaterial
        size={size}
        color={color}
        transparent
        opacity={0.6}
        sizeAttenuation
        blending={THREE.AdditiveBlending}
        depthWrite={false}
      />
    </points>
  );
}

// Secondary particle layer for depth
export function ParticleFieldSecondary({
  count = 1000,
  color = '#B08080',
}: {
  count?: number;
  color?: string;
}) {
  const meshRef = useRef<THREE.Points>(null);

  const positions = useMemo(() => {
    const positions = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const i3 = i * 3;
      positions[i3] = (Math.random() - 0.5) * 40;
      positions[i3 + 1] = (Math.random() - 0.5) * 40;
      positions[i3 + 2] = (Math.random() - 0.5) * 40 - 10;
    }
    return positions;
  }, [count]);

  useFrame((state) => {
    if (!meshRef.current) return;
    meshRef.current.rotation.y = state.clock.elapsedTime * 0.02;
    meshRef.current.rotation.z = Math.sin(state.clock.elapsedTime * 0.1) * 0.05;
  });

  return (
    <points ref={meshRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          args={[positions, 3]}
        />
      </bufferGeometry>
      <pointsMaterial
        size={0.03}
        color={color}
        transparent
        opacity={0.3}
        sizeAttenuation
        blending={THREE.AdditiveBlending}
        depthWrite={false}
      />
    </points>
  );
}
